import { Info, MoreVertical } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';
import { Spin } from 'antd';
import { useBlueprints } from '@/api/hooks/useBlueprint';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getParameterConfig } from '@/components/autopilot/parameter-config';

interface Parameter {
    id: number;
    name: string;
    display_name: string;
    color: string;
    logo: string | null;
    unit: string | null;
    device_id: string | null;
    datapoint: string | null;
    blueprints_count: number;
}

interface Blueprint {
    id: number;
    name: string;
    parameters: Parameter[];
    created_by: number;
    created_at: string;
    updated_by: number;
    updated_at: string;
}

interface ApiResponse {
    success: boolean;
    status: number;
    message: string;
    metadata: {
        timestamp: string;
        version: string;
    };
    data: Blueprint[];
}

// Format date to match the application's convention
const formatDate = (dateString: string) => {
    if (!dateString) return '';

    // Check if the date is already in a specific format
    const date = dayjs(dateString);
    if (!date.isValid()) return dateString;

    return date.format('YYYY-MM-DD');
};

export function Blueprints() {
    const [blueprints, setBlueprints] = useState<Blueprint[]>([]);
    const { data: blueprintsData, isLoading: blueprintsLoading, refetch } = useBlueprints();
    const navigate = useNavigate();

    useEffect(() => {
        refetch();
    }, [navigate]);

    useEffect(() => {
        if (!blueprintsData) return;
        // Check if the response is in the expected format
        const response = blueprintsData as unknown as ApiResponse;
        if (response.data) {
            setBlueprints(response.data);
        } else {
            // If the response is already an array of blueprints
            setBlueprints(blueprintsData as unknown as Blueprint[]);
        }
    }, [blueprintsData]);

    if (blueprintsLoading) {
        return (
          <div className="flex flex-col gap-3">
            <div className="flex justify-between items-center mb-2">
              <div className="h-6 w-28 bg-gray-200 rounded animate-pulse" />
            </div>
            
            <div className="grid gap-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="bg-white rounded-lg border border-light-grey p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="h-5 w-32 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                    </div>
                    <div className="h-4 w-4 bg-gray-200 rounded animate-pulse" />
                  </div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-20 bg-gray-200 rounded animate-pulse" />
                      <div className="flex gap-1">
                        {Array.from({ length: 3 }).map((_, j) => (
                          <div key={j} className="h-6 w-16 bg-gray-200 rounded animate-pulse" />
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-16 bg-gray-200 rounded animate-pulse" />
                      <div className="h-4 w-24 bg-gray-200 rounded animate-pulse" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
    }

    return (
        <div className="flex flex-col gap-3">

            <div className="flex justify-between items-center mb-2">
                <h2 className="heading-6 text-dark-blue">Blueprints</h2>
            </div>

            <div className="px-1">
                <div className="border border-white-blue rounded-[8px] overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-bg">
                            <tr>
                                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Blueprint Name</th>
                                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Parameters</th>
                                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Created At</th>
                                <th className="py-2 px-4 text-left paragraph-11 bg-light-grey text-dark-grey">Updated At</th>
                                <th className="py-2 px-4 bg-light-grey"></th>
                            </tr>
                        </thead>
                        <tbody>
                            {blueprints && blueprints.map((blueprint, idx) => (
                                <tr
                                    key={`${blueprint.id}`}
                                    className={cn(
                                        "border-t border-light-grey bg-white hover:bg-bg/50 hover:cursor-pointer",
                                        idx % 2 === 1 && "bg-bg",
                                    )}
                                >
                                    <td className="heading-7 px-4 text-dark-blue">{blueprint.name}</td>
                                    <td className="px-4">
                                        <div className="flex flex-wrap gap-1">
                                            {blueprint.parameters && blueprint.parameters.map((param) => {
                                                const config = getParameterConfig(param.name);

                                                return (
                                                    <div
                                                        key={param.id}
                                                        className={cn("flex items-center h-5 px-1 rounded-[4px] gap-1", config.color)}
                                                    >
                                                        {config.icon}
                                                        <span className={cn("paragraph-9 m-0", config.textColor)}>
                                                            {param.display_name}
                                                        </span>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </td>
                                    <td className="paragraph-12 text-black px-4">
                                        {formatDate(blueprint.created_at)}
                                    </td>
                                    <td className="paragraph-12 text-black px-4">
                                        {formatDate(blueprint.updated_at)}
                                    </td>
                                    <td className="py-2 px-4">
                                        <DropdownMenu>
                                            <DropdownMenuTrigger asChild>
                                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                                                    <MoreVertical className="h-4 w-4" />
                                                </Button>
                                            </DropdownMenuTrigger>
                                            <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenuItem>
                                                    <Info className="h-4 w-4 mr-2" />
                                                    <p className="paragraph-11 text-[#5E5E5E]">View Details</p>
                                                </DropdownMenuItem>
                                            </DropdownMenuContent>
                                        </DropdownMenu>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
} 